import {
  AI_MESSAGE_FOR_USER_PREFIX,
  ARTICLE_SEARCH_SYSTEM_MESSAGE_PREFIX,
  SYSTEM_MESSAGE_PREFIX,
  USER_MESSAGE_FOR_AI_PREFIX,
} from '@/common/config';
import { ItemType } from '../../lib';
import { BaseMessage, MessageListItem, UserTransactionMessage } from '../../types';
import { UserSelection, VoiceExtraField } from '@/common/api/models.pleased';
import { switcher } from '../switcher';
import { formatTimestampUTC } from '@/common/date';

/*
* We have 3 types of flow when user connect to agent:
* 1. User connect via Open Request (userSelections field in API request)
* 2. User connect via Voice Chat (extraFields field in API request)
* 3. User connect via Live Chat (queueMessage which later sends to Firebase)
*
* This difference to be refactored from BE & FE side. So we keep all selections logic here
*/

const applyPrefix = (
  prefix: string,
  message: string,
  noSystemPrefix?: boolean
): string => (prefix && !noSystemPrefix ? `${prefix} ${message}` : message);

const getCreatedAt = (timestamp?: number): string =>
  String(new Date(timestamp || Date.now()).valueOf());

// Open Request Flow selections
const userSelectionSwitcher = switcher<
  BaseMessage,
  UserSelection | UserSelection[]
>()
  .when(ItemType.isTransactionMessage, (item: UserTransactionMessage) =>
    item.issues!.map(issue => ({
      createdAt: getCreatedAt(item.timestamp),
      message: applyPrefix(
        SYSTEM_MESSAGE_PREFIX,
        `${item.message}: ${issue}`,
        item.noSystemPrefix
      ),
    }))
  )
  .when(ItemType.isSystemArticleSearchMessage, item => ({
    createdAt: getCreatedAt(item.timestamp),
    message: applyPrefix(
      ARTICLE_SEARCH_SYSTEM_MESSAGE_PREFIX,
      item.message,
      item.noSystemPrefix
    ),
  }))
  .when(ItemType.isUserMessageForAI, item => ({
    createdAt: getCreatedAt(item.timestamp),
    message: applyPrefix(
      '',
      item.message,
      item.noSystemPrefix
    ),
    isFromAI: false,
  }))
  .when(ItemType.isAiMessageForUser, item => ({
    createdAt: getCreatedAt(item.timestamp),
    message: applyPrefix(
      '',
      item.message,
      item.noSystemPrefix
    ),
    isFromAI: true,
  }))
  .none(item => ({
    createdAt: getCreatedAt(item.timestamp),
    message: applyPrefix(
      SYSTEM_MESSAGE_PREFIX,
      item.message,
      item.noSystemPrefix
    ),
  }));

const filterBaseMessage = (
  message: MessageListItem
): message is BaseMessage => ItemType.isMessage(message);

export const getUserSelections = (
  botMessages: ReadonlyArray<MessageListItem>
): UserSelection[] => {
  return botMessages
    .filter(filterBaseMessage)
    .filter(m => m.sendToChat && Boolean(m.message))
    .flatMap(m => {
      const selection = userSelectionSwitcher.match(m);
      return Array.isArray(selection) ? selection : [selection];
    });
};

// Voice Chat Flow selections
const getVoiceChatExtraFieldKey = (isFromAI: boolean | undefined): string =>
  isFromAI === undefined ? 'user-selection' : isFromAI ? 'user-selection-ai' : 'user-selection-human';

export const getVoiceChatExtraFields = (
  botMessages: ReadonlyArray<MessageListItem>
): VoiceExtraField[] => {
  const selections = getUserSelections(botMessages);
  return selections.map(selection => ({
    extraFieldKey: getVoiceChatExtraFieldKey(selection.isFromAI),
    extraFieldValue: `${selection.message} (Timestamp ${formatTimestampUTC(selection.createdAt)})`,
  }));
};

// TODO: remove this function after some time of BE transcript deployment to avoid showing
// in active conversations
export const removeMessagePrefix = (message: string): string =>
  message.replace(USER_MESSAGE_FOR_AI_PREFIX, '').replace(AI_MESSAGE_FOR_USER_PREFIX, '');

// Live Chat Flow selections
type LiveMessage = {
  message: string;
  meta: Record<string, any>;
}

const liveChatQueueMessageSwitcher = switcher<
  BaseMessage,
  LiveMessage
>()
  .when(ItemType.isTransactionMessage, (item: UserTransactionMessage) => ({
    message: applyPrefix(
      SYSTEM_MESSAGE_PREFIX,
      item.issues.length ? `${item.message} ${item.issues.join(', ')}` : item.message,
      item.noSystemPrefix
    ),
  })
  )
  .when(ItemType.isSystemArticleSearchMessage, item => ({
    message: applyPrefix(
      ARTICLE_SEARCH_SYSTEM_MESSAGE_PREFIX,
      item.message,
      item.noSystemPrefix
    ),
  }))
  .when(ItemType.isUserMessageForAI, item => ({
    message: applyPrefix(
      '',
      item.message,
      item.noSystemPrefix
    ),
    meta: { isFromAI: false },
  }))
  .when(ItemType.isAiMessageForUser, item => ({
    message: applyPrefix(
      '',
      item.message,
      item.noSystemPrefix
    ),
    // adding those fields, so we show AI response as Agent message
    meta: { isFromAI: true, from: 'agent', to: 'customer' },
  }))
  .none(item => ({
    message: applyPrefix(
      SYSTEM_MESSAGE_PREFIX,
      item.message,
      item.noSystemPrefix
    ),
  }));

export const getLiveChatQueueMessages = (
  botMessages: ReadonlyArray<MessageListItem>
): LiveMessage[] => {
  return botMessages
    .filter(filterBaseMessage)
    .filter(m => m.sendToChat && Boolean(m.message))
    .flatMap(m => liveChatQueueMessageSwitcher.match(m));
};
